import { CellContext, ColumnDefTemplate } from "@tanstack/react-table";
import { Constants } from "appConstants";
import { ApplicationListItem } from "components/BackofficeApplicationList";
import { ApplicationRegistryItem } from "components/BackofficeApplicationRegistry";
import TooltipWrapper from "components/TooltipWrapper";
import { DateTimeHelper } from "helpers";
import { last } from "helpers/ArrayHelper";
import NameHelper from "helpers/NameHelper";
import { AnalysisStatusCode } from "models/types";
import { ReactNode, useRef } from "react";
import { Tooltip } from "react-tooltip";
import { AnalysisStatusLabel } from "services/types";

export type ValueDisplay<L extends Record<string, any>, K extends keyof L> = (
	props: Readonly<{
		value: L[K];
		item?: L;
	}>
) => JSX.Element;

export function asCellRenderer<
	L extends Record<string, any>,
	K extends keyof L
>(
	ValueDisplayComponent: ValueDisplay<L, K>
): ColumnDefTemplate<CellContext<L, L[keyof L]>> {
	// eslint-disable-next-line func-names
	return function (props: CellContext<L, L[keyof L]>): JSX.Element {
		const { row, getValue } = props;
		const { original: item } = row;
		return <ValueDisplayComponent value={getValue()} item={item} />;
	};
}

export function BasicValueDisplay<V extends ReactNode>({
	value
}: Readonly<{
	value?: V | null;
}>): JSX.Element {
	return <TooltipWrapper place="top" value={value} />;
}

export function NameValueDisplay({
	value
}: Readonly<{
	value: string | null;
}>): JSX.Element {
	const abbreviatedName = NameHelper.abbreviateName(value ?? "");
	return <BasicValueDisplay value={abbreviatedName} />;
}

export function InitialLetterDisplay({
	value,
	label
}: Readonly<{
	value: string | null;
	label: string | null;
}>): JSX.Element {
	const valueRef = useRef<HTMLDivElement>(null);
	const initialLetter = label?.charAt(0);

	return (
		<div>
			<div id={`initial-letter-tooltip-${value}`} ref={valueRef}>
				{initialLetter}
			</div>
			<Tooltip anchorSelect={`#initial-letter-tooltip-${value}`} place="top">
				{label}
			</Tooltip>
		</div>
	);
}

export function FileExtensionDisplay({
	value
}: Readonly<{
	value: string | null;
}>): JSX.Element {
	return <span>{value && last(value.split("."))}</span>;
}

export function NaiveDateValueDisplay({
	value
}: Readonly<{
	value: string | null;
	item?: Record<string, any>;
}>): JSX.Element {
	if (value === null) return <span>-</span>;
	const dateString = DateTimeHelper.formatDateString(
		value,
		Constants.date.LOCALE_DATE_SHORT_YEAR_FORMAT
	);
	return <span>{dateString}</span>;
}

function BaseLocaleDateValueDisplay({
	value,
	format
}: Readonly<{
	value: string | null;
	item?: Record<string, any>;
	format?: string;
}>) {
	const dateString = value
		? DateTimeHelper.formatDate(
				DateTimeHelper.fromUtcDate(new Date(value)),
				format
		  )
		: "-";
	return <span className="w-full text-center">{dateString}</span>;
}

export function LocaleDateValueDisplay(
	props: Readonly<{
		value: string | null;
		item?: Record<string, any>;
	}>
): JSX.Element {
	return BaseLocaleDateValueDisplay({
		...props,
		format: Constants.date.LOCALE_DATE_FORMAT
	});
}

export function LocaleDateShortValueDisplay(
	props: Readonly<{
		value: string | null;
		item?: Record<string, any>;
	}>
): JSX.Element {
	return BaseLocaleDateValueDisplay({
		...props,
		format: Constants.date.LOCALE_DATE_SHORT_YEAR_FORMAT
	});
}

interface SemanticColors {
	background: string;
	text: string;
}

const warningColors: SemanticColors = {
	background: "bg-feedback-alert-high-50",
	text: "text-feedback-alert-low-950"
} as const;
const successColors: SemanticColors = {
	background: "bg-feedback-positive-high-50",
	text: "text-feedback-positive-low-950"
};
const errorColors: SemanticColors = {
	background: "bg-feedback-negative-high-50",
	text: "text-feedback-negative-low-950"
};

const pendingStatuses: string[] = [
	"em andamento",
	"mensagem recebida",
	"pendente"
];

function getNormalizedStatus(status: string | null): string {
	if (!status) return "";
	const normalized = status.trim().toLowerCase();
	if (normalized.startsWith("em andamento")) return "em andamento";
	if (normalized.startsWith("mensagem enviada")) return "mensagem enviada";
	if (normalized.startsWith("mensagem recebida")) return "mensagem recebida";
	if (normalized.startsWith("cancelado")) return "cancelado";
	if (normalized.startsWith("concluído")) return "concluído";
	if (normalized.startsWith("pendente")) return "pendente";
	if (normalized.startsWith("rascunho")) return "rascunho";
	return normalized;
}

const applicationStatusColors: Record<string, SemanticColors> = {
	"em andamento": successColors,
	"mensagem enviada": successColors,
	"mensagem recebida": warningColors,
	cancelado: errorColors,
	concluído: successColors,
	pendente: warningColors,
	rascunho: warningColors
};

export function ApplicationListStatusDisplay({
	value
}: Readonly<{
	value: string | null;
	item?: ApplicationListItem;
}>): JSX.Element {
	if (!value) return <span />;
	const normalizedStatus = getNormalizedStatus(value);
	const { text, background } =
		applicationStatusColors[normalizedStatus] || warningColors;
	return (
		<span className={`rounded-lg py-px px-2 text-xs ${text} ${background}`}>
			{value}
		</span>
	);
}

const analysisStatusColors: Record<AnalysisStatusCode, SemanticColors> = {
	APROVADO: successColors,
	APROVADO_RESSALVAS: successColors,
	CANCELADO: errorColors,
	EM_ANDAMENTO: warningColors,
	REPROVADO: errorColors
};

export function ApplicationRegistryStatusDisplay({
	value
}: Readonly<{
	value: string | null;
	item?: ApplicationRegistryItem;
}>): JSX.Element {
	if (!value) return <span />;
	const status = value as ApplicationRegistryItem["currentAnalysisStatus"];
	const { text, background } = analysisStatusColors[status];
	const statusText = AnalysisStatusLabel[status];
	return (
		<span className={`rounded-lg py-1 px-2 text-xs ${text} ${background}`}>
			{statusText}
		</span>
	);
}

function formatRemainingDaysMessage(remainingDays: number): string {
	if (remainingDays > 0) return `${remainingDays} dias`;
	if (remainingDays === 0) return "Hoje";
	const daysMessage = -remainingDays > 1 ? "dias" : "dia";
	return `Atrasado ${-remainingDays} ${daysMessage}`;
}

export function DeadlineDisplay({
	item
}: Readonly<{
	value: string | null;
	item?: ApplicationListItem;
}>): JSX.Element {
	if (!item) return <div />;
	const { daysUntilDeadline, deadlinePercentage, status } = item;
	const normalizedStatus = getNormalizedStatus(status);
	const shouldShowDeadline = pendingStatuses.find(
		(pS) => pS === normalizedStatus
	);
	if (!shouldShowDeadline) return <div />;

	const message = formatRemainingDaysMessage(Number(daysUntilDeadline));

	const bgClassName =
		Number(daysUntilDeadline) > 10
			? "bg-feedback-positive-pure-500"
			: Number(daysUntilDeadline) >= 0
			? "bg-feedback-alert-pure-500"
			: "bg-feedback-negative-pure-500";

	return (
		<div className="flex flex-col gap-1 w-full h-full items-center justify-center">
			<span>{message}</span>
			<div className="flex flex-row justify-start h-1 items-center rounded-full w-full bg-neutral-high-100">
				<span
					style={{
						width: `${Number(deadlinePercentage)}%`
					}}
					className={`h-1 rounded ${bgClassName}`}
				/>
			</div>
		</div>
	);
}

export function ProjectProgressStatusDisplay({
	value
}: Readonly<{
	value: string | null;
}>): JSX.Element {
	const progressMap: Record<string, string> = {
		LEGADO: "NÃO CLASSIFICADO",
		FUNCIONAL: "FUNCIONAL",
		EXECUTIVO: "EXECUTIVO"
	};

	return <InitialLetterDisplay value={value} label={progressMap[value!]} />;
}
